import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./shared/layout/AppShell";
import { SettingsLayout } from "./shared/layout/SettingsLayout";
import { HelpPage } from "./pages/help/HelpPage";
import { PlatformShell } from "./shared/layout/PlatformShell";
import { RequireAuth } from "./shared/auth/RequireAuth";

// Platform super-admin console (ADR-0021)
import { PlatformTenantsPage } from "./pages/platform/PlatformTenantsPage";
import { BillingSettingsPage } from "./pages/settings/BillingSettingsPage";
import { PlatformInvoiceQueuePage } from "./pages/platform/PlatformInvoiceQueuePage";
import { PlatformEInvoicePage } from "./pages/platform/PlatformEInvoicePage";
import { PlatformPaymentFeedPage } from "./pages/platform/PlatformPaymentFeedPage";
import { PlatformPlanCatalogPage } from "./pages/platform/PlatformPlanCatalogPage";
import { PlatformPlanPricePage } from "./pages/platform/PlatformPlanPricePage";
import { PlatformReconciliationPage } from "./pages/platform/PlatformReconciliationPage";
import { PlatformSaasMerchantPage } from "./pages/platform/PlatformSaasMerchantPage";
import { PlatformZaloSetupPage } from "./pages/platform/PlatformZaloSetupPage";
import { PlatformVariablesPage } from "./pages/platform/PlatformVariablesPage";
import { PlatformTenantCreatePage } from "./pages/platform/PlatformTenantCreatePage";

// Auth (auth shell)
import { LoginPage } from "./pages/auth/LoginPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { RegisteredPage } from "./pages/auth/RegisteredPage";
import { VerifyEmailPage } from "./pages/auth/VerifyEmailPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { MfaSetupPage } from "./pages/auth/MfaSetupPage";
import { MfaVerifyPage } from "./pages/auth/MfaVerifyPage";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";

// Onboarding
import { OnboardingWelcomePage } from "./pages/onboarding/OnboardingWelcomePage";
import { OnboardingClinicPage } from "./pages/onboarding/OnboardingClinicPage";
import { OnboardingServicesPage } from "./pages/onboarding/OnboardingServicesPage";
import { OnboardingDoctorsPage } from "./pages/onboarding/OnboardingDoctorsPage";
import { OnboardingSchedulePage } from "./pages/onboarding/OnboardingSchedulePage";
import { OnboardingImportPage } from "./pages/onboarding/OnboardingImportPage";
import { OnboardingDonePage } from "./pages/onboarding/OnboardingDonePage";

// Dashboard
import { DashboardPage } from "./pages/dashboard/DashboardPage";

// Patients
import { PatientListPage } from "./pages/patients/PatientListPage";
import { MessagingConsolePage } from "./pages/messaging/MessagingConsolePage";
import { PatientCreatePage } from "./pages/patients/PatientCreatePage";
import { PatientDetailPage } from "./pages/patients/PatientDetailPage";
import { PatientEditPage } from "./pages/patients/PatientEditPage";
import { PatientHistoryPage } from "./pages/patients/PatientHistoryPage";
import { PatientMergePage } from "./pages/patients/PatientMergePage";

// Appointments
import { AppointmentCalendarPage } from "./pages/appointments/AppointmentCalendarPage";
import { AppointmentDayPage } from "./pages/appointments/AppointmentDayPage";
import { AppointmentListPage } from "./pages/appointments/AppointmentListPage";
import { ReceptionPage } from "./pages/reception/ReceptionPage";
import { UnifiedAgendaPage } from "./pages/reception/UnifiedAgendaPage";
import { AppointmentCreatePage } from "./pages/appointments/AppointmentCreatePage";
import { AppointmentDetailPage } from "./pages/appointments/AppointmentDetailPage";

// Encounters
import { EncounterListPage } from "./pages/encounters/EncounterListPage";
import { EncounterWorkspacePage } from "./pages/encounters/EncounterWorkspacePage";
import { EncounterVitalsPage } from "./pages/encounters/EncounterVitalsPage";
import { EncounterNotesPage } from "./pages/encounters/EncounterNotesPage";
import { EncounterDiagnosesPage } from "./pages/encounters/EncounterDiagnosesPage";
import { EncounterPrescriptionPage } from "./pages/encounters/EncounterPrescriptionPage";
import { EncounterAttachmentsPage } from "./pages/encounters/EncounterAttachmentsPage";
import { EncounterPrintPage } from "./pages/encounters/EncounterPrintPage";

// Prescriptions
import { PrescriptionListPage } from "./pages/prescriptions/PrescriptionListPage";
import { PrescriptionDetailPage } from "./pages/prescriptions/PrescriptionDetailPage";
import { PrescriptionPrintPage } from "./pages/prescriptions/PrescriptionPrintPage";
import { PrescriptionTemplateListPage } from "./pages/prescriptions/PrescriptionTemplateListPage";
import { PrescriptionTemplateCreatePage } from "./pages/prescriptions/PrescriptionTemplateCreatePage";
import { PrescriptionTemplateEditPage } from "./pages/prescriptions/PrescriptionTemplateEditPage";

// Vaccination (P0A)
import { VaccinationWorkspacePage } from "./pages/vaccination/VaccinationWorkspacePage";
import { VaccinationBookingPage } from "./pages/vaccination/VaccinationBookingPage";
import { VaccinationCatalogPage } from "./pages/vaccination/VaccinationCatalogPage";
import { VaccinationRecordsPage } from "./pages/vaccination/VaccinationRecordsPage";
import { VaccinationObservationBoardPage } from "./pages/vaccination/VaccinationObservationBoardPage";

// Billing
import { InvoiceListPage } from "./pages/billing/InvoiceListPage";
import { InvoiceCreatePage } from "./pages/billing/InvoiceCreatePage";
import { InvoiceDetailPage } from "./pages/billing/InvoiceDetailPage";
import { InvoiceReceiptPage } from "./pages/billing/InvoiceReceiptPage";
import { PaymentListPage } from "./pages/billing/PaymentListPage";
import { ReceivablesPage } from "./pages/billing/ReceivablesPage";
import { FeedbackPage } from "./pages/feedback/FeedbackPage";
import { CouponsPage } from "./pages/coupons/CouponsPage";
import { MembershipSettingsPage } from "./pages/settings/MembershipSettingsPage";

// Reports — tải sau (#19917): Recharts chỉ cần ở nhóm màn này, không nên vào gói chính mà mọi
// màn (đăng nhập, tiếp đón) đều phải tải.
const report = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const RevenueReportPage = report(() => import("./pages/reports/RevenueReportPage"), "RevenueReportPage");
const PatientsReportPage = report(() => import("./pages/reports/PatientsReportPage"), "PatientsReportPage");
const TopServicesPage = report(() => import("./pages/reports/TopServicesPage"), "TopServicesPage");
const TopMedicinesPage = report(() => import("./pages/reports/TopMedicinesPage"), "TopMedicinesPage");
const AdvancedReportsPage = report(() => import("./pages/reports/AdvancedReportsPage"), "AdvancedReportsPage");
const OperationsReportPage = report(() => import("./pages/reports/OperationsReportPage"), "OperationsReportPage");
/** Khung chờ khi chunk báo cáo đang tải — cùng kiểu skeleton của trang báo cáo. */
const reportFallback = <div className="skeleton h-40 w-full rounded" aria-busy="true" aria-label="Đang tải" />;
const lazyReport = (el: ReactNode) => <Suspense fallback={reportFallback}>{el}</Suspense>;

// Inventory
import { StockPage } from "./pages/inventory/StockPage";
import { AdjustmentsPage } from "./pages/inventory/AdjustmentsPage";

// Me — current user
import { MyProfilePage } from "./pages/me/MyProfilePage";
import { MySecurityPage } from "./pages/me/MySecurityPage";
import { MyPreferencesPage } from "./pages/me/MyPreferencesPage";

// Settings
import { ClinicSettingsPage } from "./pages/settings/ClinicSettingsPage";
import { BranchesSettingsPage } from "./pages/settings/BranchesSettingsPage";
import { RoomsSettingsPage } from "./pages/settings/RoomsSettingsPage";
import { TreatmentPackagesSettingsPage } from "./pages/settings/TreatmentPackagesSettingsPage";
import { VerticalSettingsPage } from "./pages/settings/VerticalSettingsPage";
import { ZaloStatusPage } from "./pages/settings/ZaloStatusPage";
import { BrandingSettingsPage } from "./pages/settings/BrandingSettingsPage";
import { BankAccountSettingsPage } from "./pages/settings/BankAccountSettingsPage";
import { PaymentGatewaySettingsPage } from "./pages/settings/PaymentGatewaySettingsPage";
import { UnmatchedTransfersPage } from "./pages/billing/UnmatchedTransfersPage";
import { RefundsPage } from "./pages/billing/RefundsPage";
import { ServicesSettingsPage } from "./pages/settings/ServicesSettingsPage";
import { DepartmentsSettingsPage } from "./pages/settings/DepartmentsSettingsPage";
import { SymptomSuggestionsSettingsPage } from "./pages/settings/SymptomSuggestionsSettingsPage";
import { MedicinesSettingsPage } from "./pages/settings/MedicinesSettingsPage";
import { UsersSettingsPage } from "./pages/settings/UsersSettingsPage";
import { UserCreatePage } from "./pages/settings/UserCreatePage";
import { UserDetailPage } from "./pages/settings/UserDetailPage";
import { RolesSettingsPage } from "./pages/settings/RolesSettingsPage";
import { DoctorsSettingsPage } from "./pages/settings/DoctorsSettingsPage";
import { SchedulesSettingsPage } from "./pages/settings/SchedulesSettingsPage";
import { BookingSettingsPage } from "./pages/settings/BookingSettingsPage";
import { BookingPolicySettingsPage } from "./pages/settings/BookingPolicySettingsPage";
import { QueueCallSettingsPage } from "./pages/settings/QueueCallSettingsPage";
import { SessionsSettingsPage } from "./pages/settings/SessionsSettingsPage";
import { HolidaysSettingsPage } from "./pages/settings/HolidaysSettingsPage";
import { HisIntegrationPage } from "./pages/settings/HisIntegrationPage";
import { EInvoiceSettingsPage } from "./pages/settings/EInvoiceSettingsPage";
import { IntegrationsPage } from "./pages/settings/IntegrationsPage";
import { AiActivityPage } from "./pages/settings/AiActivityPage";
import { NotificationVariablesPage } from "./pages/settings/NotificationVariablesPage";
import { NotificationsSettingsPage } from "./pages/settings/NotificationsSettingsPage";
import { PrintTemplatesSettingsPage } from "./pages/settings/PrintTemplatesSettingsPage";
import { ExamModesSettingsPage } from "./pages/settings/ExamModesSettingsPage";
import { PrescribingSettingsPage } from "./pages/settings/PrescribingSettingsPage";
import { OperatingModeSettingsPage } from "./pages/settings/OperatingModeSettingsPage";
import { ServiceCategoriesSettingsPage } from "./pages/settings/ServiceCategoriesSettingsPage";
import { SubscriptionSettingsPage } from "./pages/settings/SubscriptionSettingsPage";
import { BackupSettingsPage } from "./pages/settings/BackupSettingsPage";
import { AuditLogPage } from "./pages/settings/AuditLogPage";
import { ConsentPage } from "./pages/settings/ConsentPage";
import { KiosksSettingsPage } from "./pages/settings/KiosksSettingsPage";
import { DisplaysSettingsPage } from "./pages/settings/DisplaysSettingsPage";

// Kiosk / waiting-room TV
import { QueueDisplayPage } from "./pages/display/QueueDisplayPage";

// Public (no-login) tokenized exam-result view
import { PublicEncounterViewPage } from "./pages/public/PublicEncounterViewPage";

// Public (no-login) patient self-service portal — phone + OTP, distinct session
import { PortalLoginPage } from "./pages/portal/PortalLoginPage";
import { PublicSitePage } from "./pages/site/PublicSitePage";
import { WebsiteDashboardPage } from "./pages/website/WebsiteDashboardPage";
import { MiniAppPage } from "./pages/miniapp/MiniAppPage";
import { PortalDashboardPage } from "./pages/portal/PortalDashboardPage";
import { PortalSlipPage } from "./pages/portal/PortalSlipPage";
import { VerifyResultPage } from "./pages/portal/VerifyResultPage";

// Errors
import { NotFoundPage } from "./pages/errors/NotFoundPage";
import { ForbiddenPage } from "./pages/errors/ForbiddenPage";
import { UpgradeRequiredPage } from "./pages/errors/UpgradeRequiredPage";

// Legacy
import { Ping } from "./pages/Ping";

function App() {
  return (
    <Routes>
      {/* Public / auth — auth shell, no sidebar */}
      {/* Kiosk / waiting-room display — no auth shell, full-bleed dark UI. */}
      <Route path="/display/queue" element={<QueueDisplayPage />} />

      {/* Public (no-login) tokenized exam-result view — reached via QR on the
          printout, e.g. /view-order/<token>/<orderCode>.html. Deliberately
          OUTSIDE RequireAuth so a logged-out patient can open it. The splat
          form (`/*`) lets the order-code segment carry the ".html" suffix. */}
      <Route path="/view-order/:token/:code" element={<PublicEncounterViewPage />} />
      <Route path="/view-order/:token/*" element={<PublicEncounterViewPage />} />

      {/* PUBLIC patient self-service portal — separate homepage, phone + OTP
          login, its own short-lived session under distinct localStorage keys.
          Deliberately OUTSIDE RequireAuth (sibling of /login, /view-order) so a
          patient never needs the staff login and never collides with a staff
          session. Not wired into the staff sidebar/nav. */}
      {/* PUBLIC clinic website. Deliberately OUTSIDE RequireAuth — a patient
          must never need a login. The slug is tenants.code; this mirrors the
          future <clinic-slug>.addy.vn hostname without needing wildcard DNS
          yet, so the routing abstraction is already correct. */}
      <Route path="/site/:slug" element={<PublicSitePage />} />
      <Route path="/site/:slug/*" element={<PublicSitePage />} />

      <Route path="/portal" element={<PortalLoginPage />} />
      <Route path="/portal/:slug" element={<PortalLoginPage />} />
      <Route path="/portal/me" element={<PortalDashboardPage />} />
      {/* Phiếu khám ngoài quét từ QR (?u=<địa chỉ phiếu>) → HIS xử lý như salepro. */}
      <Route path="/portal/slip" element={<PortalSlipPage />} />
      {/* PUBLIC — where the QR on a patient's result sheet leads (#19137). */}
      <Route path="/xac-minh/:code" element={<VerifyResultPage />} />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/registered" element={<RegisteredPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/mfa-setup" element={<MfaSetupPage />} />
      <Route path="/mfa-verify" element={<MfaVerifyPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Platform super-admin console — separate chrome, no clinic nav (ADR-0021) */}
      <Route
        element={
          <RequireAuth>
            <PlatformShell />
          </RequireAuth>
        }
      >
        <Route path="/platform" element={<Navigate to="/platform/tenants" replace />} />
        <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
        <Route path="/platform/tenants/new" element={<PlatformTenantCreatePage />} />
        {/* Cấu hình Zalo theo từng phòng khám: quản trị viên khai một lần, khách hàng không phải
            lấy gì từ Zalo. Nằm ở console nền tảng chứ không phải trang phòng khám — chính việc bắt
            khách hàng tự nhập Mini App ID là thứ trang này thay thế. */}
        <Route path="/platform/zalo" element={<PlatformZaloSetupPage />} />
        {/* Tài khoản Addy NHẬN tiền từ phòng khám (#18969). Ở console nền tảng chứ không phải
            trang phòng khám: đây là dòng tiền SaaS của Addy, và một chủ phòng khám sửa được nó
            nghĩa là chuyển hướng được toàn bộ dòng tiền đó. */}
        <Route path="/platform/thanh-toan" element={<PlatformSaasMerchantPage />} />
        {/* Đối soát tiền SaaS (#18990 P2.3). Chỉ báo, không tự sửa — sổ cái là sự thật. */}
        {/* Dòng tiền BÌNH THƯỜNG (#18990) — đối soát chỉ hiện thứ sai nên nó rỗng khi mọi thứ đúng. */}
        <Route path="/platform/thu-tien" element={<PlatformPaymentFeedPage />} />
        <Route path="/platform/doi-soat" element={<PlatformReconciliationPage />} />
        {/* Giá theo hợp đồng (#18990 P2.1) — trước đây chỉ gọi được bằng curl. */}
        {/* Bảng giá niêm yết (#18990) — khác "Giá hợp đồng" là giá riêng từng phòng khám. */}
        <Route path="/platform/goi-dich-vu" element={<PlatformPlanCatalogPage />} />
        <Route path="/platform/gia-hop-dong" element={<PlatformPlanPricePage />} />
        {/* Hàng đợi hoá đơn GTGT (#18990). Tự phát hành khi đã khai tài khoản ở /platform/hoa-don-dien-tu;
            ghi số bằng tay vẫn ở lại cho trường hợp nhà cung cấp từ chối. */}
        <Route path="/platform/hoa-don" element={<PlatformInvoiceQueuePage />} />
        {/* Tài khoản hoá đơn điện tử CỦA ADDY (#18990) — khác tài khoản của từng phòng khám. */}
        <Route path="/platform/hoa-don-dien-tu" element={<PlatformEInvoicePage />} />
        <Route path="/platform/variables" element={<PlatformVariablesPage />} />
      </Route>

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route path="/onboarding/welcome" element={<OnboardingWelcomePage />} />
        <Route path="/onboarding/clinic" element={<OnboardingClinicPage />} />
        <Route path="/onboarding/services" element={<OnboardingServicesPage />} />
        <Route path="/onboarding/doctors" element={<OnboardingDoctorsPage />} />
        <Route path="/onboarding/schedule" element={<OnboardingSchedulePage />} />
        <Route path="/onboarding/import" element={<OnboardingImportPage />} />
        <Route path="/onboarding/done" element={<OnboardingDonePage />} />

        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/help" element={<HelpPage />} />

        <Route path="/patients" element={<PatientListPage />} />
        {/* #18746 — entry point to the messaging console; the page itself mints the SSO link. */}
        <Route path="/messaging" element={<MessagingConsolePage />} />
        <Route path="/patients/new" element={<PatientCreatePage />} />
        <Route path="/patients/merge" element={<PatientMergePage />} />
        <Route path="/patients/:id" element={<PatientDetailPage />} />
        <Route path="/patients/:id/edit" element={<PatientEditPage />} />
        <Route path="/patients/:id/history" element={<PatientHistoryPage />} />

        <Route path="/reception" element={<ReceptionPage />} />
        <Route path="/agenda" element={<UnifiedAgendaPage />} />
        <Route path="/appointments" element={<AppointmentCalendarPage />} />
        <Route path="/appointments/day" element={<AppointmentDayPage />} />
        <Route path="/appointments/list" element={<AppointmentListPage />} />
        <Route path="/appointments/new" element={<AppointmentCreatePage />} />
        <Route path="/appointments/:id" element={<AppointmentDetailPage />} />

        <Route path="/encounters" element={<EncounterListPage />} />
        <Route path="/encounters/:id" element={<EncounterWorkspacePage />} />
        <Route path="/encounters/:id/vitals" element={<EncounterVitalsPage />} />
        <Route path="/encounters/:id/notes" element={<EncounterNotesPage />} />
        <Route path="/encounters/:id/diagnoses" element={<EncounterDiagnosesPage />} />
        <Route path="/encounters/:id/prescription" element={<EncounterPrescriptionPage />} />
        <Route path="/encounters/:id/attachments" element={<EncounterAttachmentsPage />} />
        <Route path="/encounters/:id/print" element={<EncounterPrintPage />} />

        <Route path="/prescriptions" element={<PrescriptionListPage />} />
        <Route path="/prescriptions/templates" element={<PrescriptionTemplateListPage />} />
        <Route path="/prescriptions/templates/new" element={<PrescriptionTemplateCreatePage />} />
        <Route path="/prescriptions/templates/:id" element={<PrescriptionTemplateEditPage />} />
        <Route path="/prescriptions/:id" element={<PrescriptionDetailPage />} />
        <Route path="/prescriptions/:id/print" element={<PrescriptionPrintPage />} />

        <Route path="/invoices" element={<InvoiceListPage />} />
        <Route path="/invoices/new" element={<InvoiceCreatePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/invoices/:id/receipt" element={<InvoiceReceiptPage />} />
        <Route path="/payments" element={<PaymentListPage />} />
        <Route path="/receivables" element={<ReceivablesPage />} />
        <Route path="/bank-transfers" element={<UnmatchedTransfersPage />} />
        <Route path="/refunds" element={<RefundsPage />} />
        <Route path="/coupons" element={<CouponsPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />

        <Route path="/reports/operations" element={lazyReport(<OperationsReportPage />)} />
        <Route path="/reports/revenue" element={lazyReport(<RevenueReportPage />)} />
        {/* #19909 — hai màn cũ gộp thành một; đường cũ (bookmark, link cũ) vào đúng chế độ. */}
        <Route path="/reports/revenue-daily" element={<Navigate to="/reports/revenue?view=day" replace />} />
        <Route path="/reports/revenue-monthly" element={<Navigate to="/reports/revenue?view=month" replace />} />
        <Route path="/reports/patients" element={lazyReport(<PatientsReportPage />)} />
        <Route path="/reports/top-services" element={lazyReport(<TopServicesPage />)} />
        <Route path="/reports/top-medicines" element={lazyReport(<TopMedicinesPage />)} />
        <Route path="/reports/advanced" element={lazyReport(<AdvancedReportsPage />)} />

        {/* Consolidated: the read-only catalog duplicated /settings/medicines. Keep the
            path as a redirect so old links/bookmarks resolve to the real stock view. */}
        <Route path="/inventory/medicines" element={<Navigate to="/inventory/stock" replace />} />
        <Route path="/inventory/stock" element={<StockPage />} />
        <Route path="/inventory/adjustments" element={<AdjustmentsPage />} />

        {/* Vaccination (P0A — ADR-0023) */}
        <Route path="/vaccination" element={<Navigate to="/vaccination/workspace" replace />} />
        <Route path="/vaccination/booking" element={<VaccinationBookingPage />} />
        <Route path="/vaccination/workspace" element={<VaccinationWorkspacePage />} />
        <Route path="/vaccination/observation" element={<VaccinationObservationBoardPage />} />
        <Route path="/vaccination/catalog" element={<VaccinationCatalogPage />} />
        <Route path="/vaccination/records" element={<VaccinationRecordsPage />} />

        {/* Kênh số — Website và Zalo Mini App là module cấp cao như Lịch hẹn hay
            Hoá đơn, KHÔNG phải trang cài đặt (ADR-0041: mỗi kênh là một channel
            riêng của nền tảng). Đặt trong SettingsLayout thì hub 3 cột không tìm
            thấy nhóm settings nào khớp, rơi vào `settingsGroups[0]` và vẽ ra tiêu
            đề "Cài đặt · Phòng khám" cùng một cột menu chẳng liên quan. */}
        <Route path="/website" element={<WebsiteDashboardPage />} />
        <Route path="/miniapp" element={<MiniAppPage />} />


        {/* Me — current user pages */}
        <Route path="/me" element={<Navigate to="/me/profile" replace />} />
        <Route path="/me/profile" element={<MyProfilePage />} />
        <Route path="/me/security" element={<MySecurityPage />} />
        <Route path="/me/preferences" element={<MyPreferencesPage />} />

        {/* #19937 — Phân ca gộp vào Lịch làm việc (bác sĩ) ở Nhân sự & Quyền. Đường cũ chỉ còn
            chuyển hướng, nên đứng NGOÀI hub: mọi route trong hub phải có mục ở thanh bên. */}
        <Route path="/settings/work-shifts" element={<Navigate to="/settings/schedules" replace />} />
        {/* Settings — 3-column hub: app sidebar + section list (SettingsLayout) + detail */}
        <Route path="/settings" element={<Navigate to="/settings/clinic" replace />} />
        <Route element={<SettingsLayout />}>
        <Route path="/settings/clinic" element={<ClinicSettingsPage />} />
        <Route path="/settings/branches" element={<BranchesSettingsPage />} />
        <Route path="/settings/rooms" element={<RoomsSettingsPage />} />
        <Route path="/settings/branding" element={<BrandingSettingsPage />} />
        <Route path="/settings/bank-account" element={<BankAccountSettingsPage />} />
        <Route path="/settings/payment-gateway" element={<PaymentGatewaySettingsPage />} />
        <Route path="/settings/services" element={<ServicesSettingsPage />} />
        <Route path="/settings/departments" element={<DepartmentsSettingsPage />} />
        <Route path="/settings/symptoms" element={<SymptomSuggestionsSettingsPage />} />
        <Route path="/settings/packages" element={<TreatmentPackagesSettingsPage />} />
        <Route path="/settings/membership" element={<MembershipSettingsPage />} />
        <Route path="/settings/vertical" element={<VerticalSettingsPage />} />
        <Route path="/settings/zalo" element={<ZaloStatusPage />} />
        <Route path="/settings/medicines" element={<MedicinesSettingsPage />} />
        <Route path="/settings/medicines/:medicineId" element={<MedicinesSettingsPage />} />
        <Route path="/settings/users" element={<UsersSettingsPage />} />
        <Route path="/settings/users/new" element={<UserCreatePage />} />
        <Route path="/settings/users/:id" element={<UserDetailPage />} />
        <Route path="/settings/roles" element={<RolesSettingsPage />} />
        <Route path="/settings/doctors" element={<DoctorsSettingsPage />} />
        <Route path="/settings/schedules" element={<SchedulesSettingsPage />} />
        <Route path="/settings/booking-policy" element={<BookingPolicySettingsPage />} />
        <Route path="/settings/sessions" element={<SessionsSettingsPage />} />
        <Route path="/settings/booking-limits" element={<BookingSettingsPage />} />
        <Route path="/settings/queue-calling" element={<QueueCallSettingsPage />} />
        <Route path="/settings/holidays" element={<HolidaysSettingsPage />} />
        <Route path="/settings/integrations" element={<IntegrationsPage />} />
        <Route path="/settings/his-integration" element={<HisIntegrationPage />} />
        <Route path="/settings/einvoice" element={<EInvoiceSettingsPage />} />
        <Route path="/settings/ai-activity" element={<AiActivityPage />} />
        <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/settings/notification-variables" element={<NotificationVariablesPage />} />
        <Route path="/settings/print-templates" element={<PrintTemplatesSettingsPage />} />
        <Route path="/settings/service-categories" element={<ServiceCategoriesSettingsPage />} />
        <Route path="/settings/exam-modes" element={<ExamModesSettingsPage />} />
        <Route path="/settings/prescribing" element={<PrescribingSettingsPage />} />
        <Route path="/settings/operating-mode" element={<OperatingModeSettingsPage />} />
        <Route path="/settings/subscription" element={<SubscriptionSettingsPage />} />
        {/* Ví và thanh toán (#18974). KHÔNG gác sau quyền lợi gói: đây là đường duy nhất để một
            phòng khám đang nợ tiền thoát khỏi việc nợ tiền. */}
        <Route path="/settings/billing" element={<BillingSettingsPage />} />
        <Route path="/settings/backup" element={<BackupSettingsPage />} />
        <Route path="/settings/audit-log" element={<AuditLogPage />} />
        <Route path="/settings/consent" element={<ConsentPage />} />
        <Route path="/settings/kiosks" element={<KiosksSettingsPage />} />
        <Route path="/settings/displays" element={<DisplaysSettingsPage />} />
        </Route>

        {/* Legacy ping kept under app shell for QA/debug */}
        <Route path="/_meta/ping" element={<Ping />} />
      </Route>

      {/* Error pages — accessible without auth so deep links land cleanly */}
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/402" element={<UpgradeRequiredPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
